import SubscriptionProducts from '../enums/SubscriptionProducts';
import UserRole from '../enums/UserRole';

class UserInfo {
    get userId() {
        return this._userId;
    }

    set userId(userId) {
        this._userId = userId;
    }

    get name() {
        return this._name;
    }

    set name(name) {
        this._name = name;
    }

    get product() {
        return this._product;
    }

    set product(product) {
        this._product = product;
    }

    get isProUser() {
        return this.isPro();
    }

    get isStarterTierUser() {
        return this.isStarter();
    }

    get isLogged() {
        return this.userId !== -1;
    }

    get licenseInfo() {
        return this._licenseInfo;
    }

    set licenseInfo(licenseInfo) {
        this._licenseInfo = licenseInfo;
    }

    get activeSubscriptions() {
        return this._activeSubscriptions;
    }

    set activeSubscriptions(activeSubscriptions) {
        this._activeSubscriptions = activeSubscriptions;
    }

    get organizations() {
        return this._organizations;
    }

    set organizations(organizations) {
        this._organizations = organizations;
    }

    get activeOrganizationId() {
        return this._activeOrganizationId;
    }

    set activeOrganizationId(activeOrganizationId) {
        this._activeOrganizationId = activeOrganizationId;
    }

    /** @type {import('./UserPreferences').default} */
    get preferences() {
        return this._preferences;
    }

    set preferences(preferences) {
        this._preferences = preferences;
    }

    /**
     * Get JSON representation of the userInfo
     * @returns {object} JSON representation of the userInfo
     */
    toJSON() {
        return {
            userId: this.userId,
            name: this.name,
            product: this.product,
            licenseInfo: this.licenseInfo,
        };
    }

    hasRole(role) {
        return this.product.toLowerCase() === role.toLowerCase();
    }

    hasAnyRole(roles) {
        // allow all to students for now
        const userInfo = this;
        if (userInfo.hasRole(UserRole.PRODUCT_STUDENT)) return true;
        // if it is PRO user and array of roles is empty return true
        if (userInfo.hasRole(UserRole.PRODUCT_PRO) && roles.length === 0) return true;
        return roles.some(roleName => userInfo.hasRole(roleName));
    }

    isMax() {
        return this.activeSubscriptionsContainProduct(
            SubscriptionProducts.ApplicationAccess.TRACTIQ_SELF_STORAGE_MAX,
        );
    }

    isProPlus() {
        return this.activeSubscriptionsContainProduct(
            SubscriptionProducts.ApplicationAccess.TRACTIQ_SELF_STORAGE_PRO_PLUS,
        );
    }

    isPro() {
        return this.activeSubscriptionsContainProduct(
            SubscriptionProducts.ApplicationAccess.TRACTIQ_SELF_STORAGE,
        );
    }

    isStarter() {
        return (
            !this.isMax() &&
            !this.isProPlus() &&
            !this.isPro() &&
            this.activeSubscriptionsContainProduct(
                SubscriptionProducts.ApplicationAccess.TRACTIQ_SELF_STORAGE_STARTER_TIER,
            )
        );
    }

    getUserProduct() {
        return this.isMax()
            ? SubscriptionProducts.ProductTypes.MAX_PRODUCT
            : this.isProPlus()
              ? SubscriptionProducts.ProductTypes.PRO_PLUS_PRODUCT
              : this.isPro()
                ? SubscriptionProducts.ProductTypes.PROFESSIONAL_PRODUCT
                : this.isStarter()
                  ? SubscriptionProducts.ProductTypes.STARTER_PRODUCT
                  : SubscriptionProducts.ProductTypes.OTHER;
    }

    activeSubscriptionsContainProduct(product) {
        if (!this.activeSubscriptions) {
            return false;
        }
        return this.activeSubscriptions.some(subscription => {
            return subscription.products.map(product => product.productId).includes(product);
        });
    }

    hasFeatureToggle = featureId => {
        return this.activeSubscriptions.some(subscription =>
            subscription.products.some(
                product =>
                    product.configuration.features &&
                    product.configuration.features.includes(featureId),
            ),
        );
    };
}

export default UserInfo;
